import { useContext } from 'react';
import TextField from 'components/forms/textField/TextField';
import { useTranslation } from 'react-i18next';
import { Col, ControlLabel, Row } from 'react-bootstrap';
import Button from 'components/button/Button';
import SelectField from 'components/forms/selectField/SelectField';
import {
  getAttributableChannels,
  getAttributedChannelSelectOptions,
  getLeadRentalAgreementDocumentUrl,
} from 'utils/lead/leadUtils';
import { LeadSource } from 'models/Leads';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { CustomDataFieldUsage } from 'models/CustomDataField';
import LeadModalContext from '../../LeadModalContext';
import {
  CustomDataColumn,
  CustomDataPageHeader,
  CustomDataWrapper,
  LeadDataFormColumn,
  LeadDataFormRow,
} from './LeadModalDataTab.styles';
import LeadCustomData from './LeadCustomData';
import BookingComData from './BookingComData';

const LeadModalDataTab = () => {
  const { lead, channelSettings } = useContext(LeadModalContext);
  const { t } = useTranslation();
  const { getCopyAddonIcon } = useCopyToClipboard();

  const attributableChannels = getAttributableChannels(lead, channelSettings);
  const channelOptions = getAttributedChannelSelectOptions(
    lead,
    channelSettings,
  );
  const showAirbnbData =
    lead?.source === LeadSource.DIRECT_AIRBNB && lead?.externalBookingID;
  const showVRBOData =
    lead?.source === LeadSource.DIRECT_HOMEAWAY && lead?.externalBookingID;
  const showBookingComData =
    lead?.source === LeadSource.DIRECT_BOOKINGDOTCOM &&
    lead?.externalBookingID &&
    lead?.bookingDotComData;
  const showCustomData =
    // @ts-expect-error
    lead?.customData?.filter(
      (singleCustomData) =>
        singleCustomData.usage === CustomDataFieldUsage.CUSTOM_VARIABLES,
    ).length > 0;

  return (
    <Row>
      <LeadDataFormColumn md={10}>
        <LeadDataFormRow>
          <TextField
            disabled
            label={t('componentLead.modal.dataTab.uid')}
            name="uid"
            // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            rightAddons={[getCopyAddonIcon(lead?.uid)]}
            colSmInput={5}
            colSmLabel={3}
          />
        </LeadDataFormRow>

        {lead?.agreementSigned && (
          <LeadDataFormRow style={{ display: 'flex', alignItems: 'center' }}>
            <Col xs={4} sm={3}>
              <ControlLabel style={{ textAlign: 'right', width: '100%' }}>
                {t('componentLead.modal.dataTab.rentalAgreement')}
              </ControlLabel>
            </Col>
            <Col xs={8} sm={5}>
              <Button
                $colorScheme="green"
                data-testid="rental-agreement-download"
                href={getLeadRentalAgreementDocumentUrl(lead?.uid)}
                rel="noopener noreferrer"
                target="_blank"
              >
                {t('componentLead.modal.dataTab.rentalAgreementDownload')}
              </Button>
            </Col>
          </LeadDataFormRow>
        )}

        <TextField
          disabled
          label={t('componentLead.modal.dataTab.createMetadata')}
          name="creationMetadata"
          rightAddons={
            !!lead?.creationMetadata && [
              getCopyAddonIcon(lead?.creationMetadata),
            ]
          }
          colSmInput={5}
          colSmLabel={3}
        />

        <LeadDataFormRow>
          <SelectField
            name="attributedChannel"
            label={t('componentLead.modal.dataTab.attributedChannelLabel')}
            disabled={attributableChannels.length === 0}
            options={[
              {
                value: '',
                label: t('componentLead.modal.dataTab.attributedChannelOption'),
              },
              ...channelOptions.map((channelOption) => ({
                value: channelOption.value,
                label: t(
                  'componentLead.modal.dataTab.attributedChannelOption',
                  { context: channelOption.channelEnumId },
                ),
              })),
            ]}
            colSmInput={3}
            colSmLabel={3}
          />
        </LeadDataFormRow>
      </LeadDataFormColumn>

      {(showAirbnbData || showVRBOData) && (
        <CustomDataWrapper data-testid="custom-data-wrapper-airbnb">
          <CustomDataColumn md={12}>
            <CustomDataPageHeader
              title={`${
                showAirbnbData
                  ? t('componentLead.modal.dataTab.customDataAirbnbTitle')
                  : t('componentLead.modal.dataTab.customDataVRBOTitle')
              }`}
            />
          </CustomDataColumn>
          <CustomDataColumn md={10}>
            <LeadDataFormRow>
              <TextField
                disabled
                label={t('componentLead.modal.dataTab.reservationID')}
                name="externalBookingID"
                rightAddons={[
                  getCopyAddonIcon(
                    // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
                    lead?.externalBookingID ||
                      lead?.bookingDotComData?.reservationId,
                  ),
                ]}
                colSmInput={5}
                colSmLabel={3}
              />
            </LeadDataFormRow>
          </CustomDataColumn>
        </CustomDataWrapper>
      )}

      {showBookingComData && <BookingComData lead={lead} />}

      {showCustomData && (
        <CustomDataWrapper data-testid="custom-data-wrapper-custom">
          <CustomDataColumn md={12}>
            <CustomDataPageHeader
              title={`${t('componentLead.modal.dataTab.customDataTitle')}`}
              subtitle={
                <span>
                  {`${t('componentLead.modal.dataTab.customDataSubtitle')} `}
                  <a
                    href="#/my-templates#customdata"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('componentLead.modal.dataTab.customDataSubtitleLink')}
                  </a>
                </span>
              }
            />
          </CustomDataColumn>
          <CustomDataColumn md={10}>
            <LeadCustomData />
          </CustomDataColumn>
        </CustomDataWrapper>
      )}
    </Row>
  );
};

export default LeadModalDataTab;
